"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import type { SearchEntry } from "@/lib/categories/category-search";
import type { CategoryStatus } from "@/lib/categories/tree-model";

interface CategorySearchResultsProps {
  statuses: ReadonlyMap<string, CategoryStatus>;
  results: SearchEntry[];
  selectedId: string | null;
  /** Row the arrow keys are currently on. */
  activeIndex: number;
  truncated: boolean;
  onSelect: (id: string) => void;
}

function highlight(text: string, range: [number, number] | null) {
  if (!range) return text;
  const [from, to] = range;
  return (
    <>
      {text.slice(0, from)}
      <mark className="rounded-sm bg-amber-200 px-0.5 text-foreground">
        {text.slice(from, to)}
      </mark>
      {text.slice(to)}
    </>
  );
}

export function CategorySearchResults({
  statuses,
  results,
  selectedId,
  activeIndex,
  truncated,
  onSelect,
}: CategorySearchResultsProps) {
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!results.length) {
    return (
      <p className="px-2 py-4 text-center text-xs text-muted-foreground">
        No category matches that.
      </p>
    );
  }

  return (
    <div role="listbox" aria-label="Search results" className="px-1.5 pb-3">
      {results.map((entry, i) => (
        <button
          key={entry.id}
          ref={i === activeIndex ? activeRef : undefined}
          type="button"
          role="option"
          aria-selected={entry.id === selectedId}
          className={cn(
            "block w-full rounded-md px-2 py-1.5 text-left",
            i === activeIndex && "bg-muted",
            entry.id === selectedId && "bg-pink-50",
          )}
          onClick={() => onSelect(entry.id)}
        >
          <span
            className={cn(
              "block truncate text-sm",
              entry.id === selectedId && "font-medium text-pink-700",
            )}
          >
            {highlight(entry.name, entry.nameMatch)}
          </span>
          {statuses.get(entry.id) !== "Enabled" && (
            <span className="block text-xs text-muted-foreground">{statuses.get(entry.id)}</span>
          )}
          <span className="block truncate text-[11px] text-muted-foreground">
            {entry.path.length
              ? highlight(entry.path.join(" › "), entry.pathMatch)
              : "Top level"}
          </span>
        </button>
      ))}
      {truncated && (
        <p className="px-2 py-3 text-center text-xs text-muted-foreground">
          Showing the first 200 matches — refine to narrow.
        </p>
      )}
    </div>
  );
}
